import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { Container, PageHero, CTASection } from "@/components/primitives";
import { PROJECTS } from "@/data/projects";

export default function Projects() {
  return (
    <>
      <SEO
        title="Projects"
        description="Case studies from Vivek Chandra: OrchestrIQ engineering intelligence, the Ichi & Ori Pet Care digital platform, and TrueQual Dataworks. Business impact first, engineering depth throughout."
        path="/projects"
      />

      <PageHero label="Selected Work" title="Case studies, not screenshots.">
        Every project here is presented as a case study. The business problem,
        the decisions, the trade-offs and the outcome. Real software, built to
        solve real problems.
      </PageHero>

      <section className="py-20 md:py-28" data-testid="projects-list">
        <Container>
          <div className="space-y-6">
            {PROJECTS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.05}>
                <Link
                  to={`/projects/${p.slug}`}
                  data-testid={`project-card-${p.slug}`}
                  className="group grid grid-cols-1 gap-8 border border-white/10 p-6 transition-colors hover:border-white/25 md:grid-cols-12 md:p-8"
                >
                  <div className="overflow-hidden md:col-span-6">
                    <img
                      src={p.cover}
                      alt={p.name}
                      loading="lazy"
                      className="aspect-[16/10] w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                  </div>
                  <div className="flex flex-col justify-center md:col-span-6">
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-ink">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="h-px flex-1 bg-white/10" />
                      <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-ink">
                        {p.role} · {p.year}
                      </span>
                    </div>
                    <h2 className="mt-6 font-cabinet text-3xl font-medium tracking-tight text-primary-ink md:text-4xl">
                      {p.name}
                    </h2>
                    <p className="mt-2 font-mono text-xs uppercase tracking-[0.12em] text-accent">
                      {p.tagline}
                    </p>
                    <p className="mt-5 max-w-xl leading-relaxed text-secondary-ink">
                      {p.summary}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {p.tech.slice(0, 4).map((t) => (
                        <span
                          key={t}
                          className="border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-ink"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <span className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-primary-ink">
                      Read case study
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <CTASection />
    </>
  );
}
